import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createVatRate, updateVatRate, type VatRate } from '../../api/taxe'
import { errorMessage } from '../../api/client'

const schema = z.object({
  code: z.string().min(1, 'Câmp obligatoriu').max(32),
  name: z.string().min(1, 'Câmp obligatoriu').max(64),
  ratePercent: z.string().min(1, 'Câmp obligatoriu'),
  treatmentType: z.enum(['Standard', 'CotaRedusa', 'TaxareInversa', 'Scutit', 'IntracomunitarUE', 'Export']),
  effectiveFrom: z.string().min(1, 'Câmp obligatoriu'),
  effectiveTo: z.string().optional(),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = { code: '', name: '', ratePercent: '19', treatmentType: 'Standard', effectiveFrom: '', effectiveTo: '' }

const TREATMENTS: { value: FormValues['treatmentType']; label: string }[] = [
  { value: 'Standard', label: 'Standard' },
  { value: 'CotaRedusa', label: 'Cotă redusă' },
  { value: 'TaxareInversa', label: 'Taxare inversă' },
  { value: 'Scutit', label: 'Scutit' },
  { value: 'IntracomunitarUE', label: 'Intracomunitar UE' },
  { value: 'Export', label: 'Export' },
]

export default function VatRateFormDialog({ rate, onClose }: { rate: VatRate | null | 'new'; onClose: () => void }) {
  const open = rate !== null
  const editing = rate !== null && rate !== 'new'
  const qc = useQueryClient()

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema), defaultValues: EMPTY,
  })

  useEffect(() => {
    if (editing) {
      const c = rate as VatRate
      reset({ code: c.code, name: c.name, ratePercent: String(c.ratePercent), treatmentType: c.treatmentType, effectiveFrom: c.effectiveFrom, effectiveTo: c.effectiveTo ?? '' })
    } else if (rate === 'new') {
      reset(EMPTY)
    }
  }, [rate, editing, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload = { ...values, ratePercent: Number(values.ratePercent), effectiveTo: values.effectiveTo || null }
      if (editing) return updateVatRate((rate as VatRate).id, payload)
      return createVatRate(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['financiar', 'cote-tva'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare cotă TVA' : 'Cotă TVA nouă'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={2}>
              <Controller name="code" control={control} render={({ field }) => (
                <TextField {...field} label="Cod" disabled={editing} fullWidth error={!!errors.code} helperText={errors.code?.message} />
              )} />
              <Controller name="name" control={control} render={({ field }) => (
                <TextField {...field} label="Denumire" fullWidth error={!!errors.name} helperText={errors.name?.message} />
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="ratePercent" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Cotă (%)" fullWidth error={!!errors.ratePercent} helperText={errors.ratePercent?.message} />
              )} />
              <Controller name="treatmentType" control={control} render={({ field }) => (
                <TextField {...field} select label="Tratament fiscal" fullWidth error={!!errors.treatmentType} helperText={errors.treatmentType?.message}>
                  {TREATMENTS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </TextField>
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="effectiveFrom" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Valabil de la" fullWidth slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.effectiveFrom} helperText={errors.effectiveFrom?.message} />
              )} />
              <Controller name="effectiveTo" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Valabil până la" fullWidth slotProps={{ inputLabel: { shrink: true } }} />
              )} />
            </Stack>
            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
